import React, { useState, useRef, useEffect, useCallback, MutableRefObject } from "react";
import "./DetailsList.css";
import DetailsItem from "../details-item/DetailsItem";
import BusinessEntity from "../../../models/BusinessEntity";
import { Link } from "react-router-dom";
import ContactForm from "../contact-form/ContactForm";
import { watch, when } from "@arcgis/core/core/reactiveUtils.js";
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import { intersect } from "@arcgis/core/geometry/geometryEngine";
import Geometry from "@arcgis/core/geometry/Geometry";

import Filters from "../filters/Filters";
import errorLogger from "../../../utilities/errorLogger";

interface iProps {
  view: React.MutableRefObject<__esri.MapView>;
  filterByMapExtent: boolean;
  stateFilterGeometry: __esri.Polygon | null;
  radiusFilterGeometry: __esri.Polygon | null;
  filters?: React.RefObject<React.ElementRef<typeof Filters>>;
  stateOptions: { value: string; label: string }[]
  classIOptions: string[];
};

const highlightLayerId: string = 'DetailsListHighlight';

export default function DetailsList(props: iProps) {
  const [businessEntities, setBusinessEntities] = useState<BusinessEntity[] | undefined>();
  const [showContactUs, setShowContactUs] = useState(false);
  const [expandedItem, setExpandedItem] = useState<BusinessEntity | null>(null);
  const layersInitialized = useRef(false);
  const businessEntityLayer: MutableRefObject<__esri.FeatureLayer | undefined> = useRef();
  const interchangeLayer: MutableRefObject<__esri.FeatureLayer | undefined> = useRef();
  const railroadGroupLayer: MutableRefObject<__esri.GroupLayer | undefined> = useRef();
  const locationMarketingGroupLayer: MutableRefObject<__esri.GroupLayer | undefined> = useRef();
  const filtersUpdateWatchHandle: MutableRefObject<__esri.WatchHandle | undefined> = useRef();
  const viewUpdateWatchHandle: MutableRefObject<__esri.WatchHandle | undefined> = useRef();
  const viewExtent = useRef(props.view.current?.extent);
  const highlightLayer = useRef<__esri.GraphicsLayer>(
    new GraphicsLayer({
      id: highlightLayerId,
      listMode: 'hide'
    })
  );
  const highlightSymbol: MutableRefObject<__esri.SimpleFillSymbol | undefined> = useRef();
  const abortController = useRef<AbortController>();

  const queryBusinessEntities = useCallback((where: string) => {

    let geometry: __esri.Geometry | null = null;
    if (props.filterByMapExtent) {
      if (props.stateFilterGeometry) {
        const intersectResult = intersect(viewExtent.current, props.stateFilterGeometry);
        if (intersectResult === null) {
          // no intersection
          setBusinessEntities([]);
          return;
        } else if (intersectResult instanceof Geometry) {
          geometry = intersectResult
        } else if (intersectResult && intersectResult.length > 0) {
          geometry = intersectResult[0];
        }
      } else {
        geometry = viewExtent.current;
      }

      if (props.radiusFilterGeometry) {
        if (geometry) {
          const intersectResult = intersect(geometry, props.radiusFilterGeometry)
          if (intersectResult instanceof Geometry || intersectResult == null) {
            geometry = intersectResult
          } else if (intersectResult.length > 0) {
            geometry = intersectResult[0];
          }
        } else {
          geometry = props.radiusFilterGeometry;
        }
      }

    } else if (props.stateFilterGeometry) {
      geometry = props.stateFilterGeometry;
    } else if (props.radiusFilterGeometry) {
      geometry = props.radiusFilterGeometry;
    }

    businessEntityLayer.current?.queryFeatures(
      {
        where: `GISStorageCapacity > 0 AND (${where})`,
        returnGeometry: true,
        // outFields: ['*'],
        outFields: ['OBJECTID', 'GPRailroadMark', 'REFXCompanyCode', 'GPPublicName', 'GISStorageCapacity'],
        geometry: geometry ?? undefined,
        returnDistinctValues: true,
        orderByFields: ['GPPublicName ASC']
      },
      { signal: abortController.current?.signal })
      .then(featureSet => {
        const bizEntities = featureSet.features.map(feature => {
          const atts = feature.attributes;
          return {
            featureId: atts.OBJECTID,
            businessEntityId: atts.REFXCompanyCode,
            publicName: atts.GPPublicName,
            capacity: atts.GISStorageCapacity,
            geometry: feature.geometry
          };
        })
        // adding filter to remove duplicates
        const bizEntityIds = new Set(bizEntities.map(x => x.businessEntityId));
        const uniqueBizEntities = Array.from(bizEntityIds)
          .map(id => bizEntities.find(x => x.businessEntityId === id))
          .filter(x => x !== undefined);
        setBusinessEntities(uniqueBizEntities as BusinessEntity[]);
      })
      .catch(errorLogger);
  }, [props.filterByMapExtent, props.stateFilterGeometry, props.radiusFilterGeometry])

  const updateList = useCallback(() => {
    if (!businessEntityLayer.current || !interchangeLayer.current) return;

    const interchangeLayerWhere = interchangeLayer.current.definitionExpression
      ? interchangeLayer.current.definitionExpression : "1=1";

    if (abortController.current) {
      abortController.current.abort();
    }
    abortController.current = new AbortController();

    if (interchangeLayerWhere === "1=1") {
      queryBusinessEntities("1=1");
    } else {
      interchangeLayer.current.queryFeatures(
        {
          where: interchangeLayerWhere,
          returnGeometry: false,
          outFields: ["railroad"],
          returnDistinctValues: true
        }, { signal: abortController.current.signal })
        .then(featureSet => {
          if (featureSet.features.length === 0) {
            setBusinessEntities([]);
          } else {
            const railroads = featureSet.features
              .map(feature => `'${feature.attributes.railroad}'`).join(',');
            const where = `GPRailroadMark IN (${railroads}) OR REFXCompanyCode IN (${railroads})`;
            queryBusinessEntities(where);
          }
        })
        .catch(errorLogger);
    }
  }, [queryBusinessEntities])


  const initLayers = () => {
    if (!layersInitialized.current) {
      layersInitialized.current = true;
      props.view.current.when(() => {
        businessEntityLayer.current = props.view.current.map.layers
          .find(layer => layer.title === 'Business Entity') as __esri.FeatureLayer;
        interchangeLayer.current = props.view.current.map.layers
          .find(layer => layer.title === 'Interchange') as __esri.FeatureLayer;

        railroadGroupLayer.current = props.view.current.map.layers
          .find(layer => layer.title === 'Railroads') as __esri.GroupLayer;

        locationMarketingGroupLayer.current = props.view.current.map.layers
          .find(layer => layer.title === 'LocationMarketingLayers') as __esri.GroupLayer;

        if (businessEntityLayer.current && interchangeLayer.current) {
          if (interchangeLayer.current) {
            // filtersUpdateWatchHandle.current = watchUtils.watch(interchangeLayer.current, 'definitionExpression', updateList);
            filtersUpdateWatchHandle.current = watch(() => interchangeLayer.current?.definitionExpression, updateList);
          }

          updateList();
        }
      });

      if (!props.view.current.map.findLayerById(highlightLayerId)) {
        props.view.current.map.add(highlightLayer.current);

        const highlightOptions = props.view.current.highlightOptions;
        const color = highlightOptions.color;
        if (color) {
          highlightSymbol.current = new SimpleFillSymbol({
            color: [color.r, color.g, color.b, highlightOptions.fillOpacity || 0.2],
            style: 'solid',
            outline: new SimpleLineSymbol({
              color: [color.r, color.g, color.b, highlightOptions.haloOpacity || 1],
              width: 1,
              style: 'solid'
            })
          });
        }
      }

    };
  }
  useEffect(initLayers, [props.view, updateList])


  const initFilterUpdateWatchers = () => {

    if (interchangeLayer.current) {
      filtersUpdateWatchHandle.current?.remove();
      filtersUpdateWatchHandle.current = watch(() => interchangeLayer.current?.definitionExpression, updateList);
    }

    return () => {
      if (filtersUpdateWatchHandle.current) {
        filtersUpdateWatchHandle.current.remove();
      }
    }
  }
  useEffect(initFilterUpdateWatchers, [props.view, updateList]);

  const initExtentUpdateWatcher = () => {
    updateList();

    if (viewUpdateWatchHandle.current) {
      viewUpdateWatchHandle.current.remove();
    }
    if (props.filterByMapExtent) {

      viewUpdateWatchHandle.current = when(
        () => props.view.current.stationary,
        () => {
          if (viewExtent.current !== props.view.current.extent) {
            viewExtent.current = props.view.current.extent;
            updateList();
          }
        });
    }
  }
  useEffect(initExtentUpdateWatcher, [props.view, props.filterByMapExtent, updateList]);

  const onContactUsClick = (publicName: string) => () => {
    setShowContactUs(true);
  }

  const hideContactUs = () => {
    setShowContactUs(false);
  }

  const expandDetailItem = (entity: BusinessEntity) => () => {
    setExpandedItem(entity);
    highlightFeature(entity)();
  }

  const showFullList = () => {
    setExpandedItem(null);
    highlightLayer.current.removeAll();
  }

  const highlightFeature = (entity: BusinessEntity) => () => {
    highlightLayer.current?.removeAll();
    const graphic = new Graphic({
      geometry: entity.geometry,
      symbol: highlightSymbol.current,
      attributes: { id: entity.businessEntityId }
    });
    highlightLayer.current.add(graphic);
  }

  const removeFeatureHighlight = () => {
    highlightLayer.current?.removeAll();
  }

  return (
    <div className="railcar-storage-details-list-container">
      {!showContactUs &&
        <div className="contact-info" >
          <span className="bold">Contact Railcar Storage</span>
          <span>David L. McCloud</span>
          <span>storage@watco.com</span>
          <a href="tel:9043035966">(904) 303-5966</a>
        </div>
      }
      <div className="railcar-storage-details-list">
        {
          !businessEntities &&
          <div className="spinner-container">
            <div className='spinner'></div>
            <div>Loading...</div>
          </div>
        }
        {businessEntities?.length === 0 &&
          <div>
            <h4>No results found.</h4>
            <p>Is there something you're looking for but don't see it here? <Link to="/contact-us">Contact us.</Link></p>
          </div>
        }
        {showContactUs &&
          <div className="contact-us-form-container">
            <ContactForm onSubmitCallback={hideContactUs}
              stateOptions={props.stateOptions}
              classIOptions={props.classIOptions}
              filters={props.filters}
            />
            <div>
              <button onClick={hideContactUs}>Back to List</button>
            </div>
          </div>
        }
        {!showContactUs && expandedItem &&
          <div className="expanded-details-container">
            <DetailsItem
              businessEntity={expandedItem}
              key='-1'
              expanded={true}
              zoomToHandler={() => { props.view.current?.goTo(expandedItem.geometry) }}
              interchangeLayer={interchangeLayer}
              onContactUsClick={onContactUsClick(expandedItem.publicName)} />
            <div>
              <button onClick={showFullList}>Show Full List</button>
            </div>
          </div>
        }
        {!showContactUs && !expandedItem &&
          businessEntities?.map((entity, index) =>
            <DetailsItem
              businessEntity={entity}
              key={index}
              expanded={false}
              expandHandler={expandDetailItem(entity)}
              zoomToHandler={() => { props.view.current?.goTo(entity.geometry) }}
              onMouseEnter={highlightFeature(entity)}
              onMouseLeave={removeFeatureHighlight}
              onContactUsClick={onContactUsClick(entity.publicName)} />
          )
        }
      </div>
      <button className="bg-gray-6 no-border" onClick={props.filters?.current?.reset}>Reset</button>
    </div>
  )
}